<template>
  <div class="fu-drtab" data-test="fu-drawer-definitions">
    <template v-for="g in groups">
      <div :key="`${g.key}-t`" class="fu-drtab__title">{{ g.title }}</div>
      <dl v-if="g.pairs" :key="`${g.key}-d`" class="fu-drdef" :data-test="`fu-drawer-def-${g.key}`">
        <div v-for="p in g.pairs" :key="p.k" class="fu-drdef__cell">
          <dt>{{ p.k }}</dt>
          <dd>{{ p.v }}</dd>
        </div>
      </dl>
      <div v-else :key="`${g.key}-l`" :data-test="`fu-drawer-def-${g.key}`">
        <p v-if="!g.items.length" class="fu-drtab__none">No procedures on this pre-authorization.</p>
        <div v-for="(it, i) in g.items" :key="i" class="fu-drtab__item">
          <span class="fu-drtab__code">{{ it.code }}</span>
          <div class="fu-drtab__main">
            <div class="fu-drtab__item-title">{{ it.title }}</div>
            <div v-if="it.meta" class="fu-drtab__meta">{{ it.meta }}</div>
          </div>
          <div class="fu-drtab__right"><strong>{{ it.right }}</strong><small>Ins est</small></div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * DrawerDefinitionsTab — the design's two-column definitions for "Claim" and "Pre-auth", filled from
 * today's tabs (drawerTabs.CLAIM_TAB_FIELDS):
 *   claim (claims)                Claim Information + Subscriber Information
 *   preauth / preauth (outstanding)  Outstanding PreAuthorization Info + Carrier and Subscriber Info
 *   preauth / procedures (processed) PreAuthorization Info — the procedure list
 * Subscriber name / DOB / ID are shown as loaded: masked at the data boundary (phiMask), never here.
 * Presentational.
 */
export default {
  name: 'DrawerDefinitionsTab',
  props: {
    tab: { type: Object, required: true },
    ctx: { type: Object, required: true },
  },
  computed: {
    groups() {
      const c = this.ctx;
      const d = v => (v ? c.formatDate(v) : '—');
      const t = v => (v === null || v === undefined || String(v).trim() === '' ? '—' : String(v));
      const m = v => (v === null || v === undefined || v === '' ? '—' : c.formatMoney(v));
      const out = [];
      (this.tab.sections || []).forEach((s) => {
        const row = (s.rows || [])[0] || {};
        if (s.key === 'claim') {
          out.push({ key: 'claim', title: 'Claim', pairs: [
            { k: 'Provider', v: t(row.ProviderName) }, { k: 'Claim sent', v: d(row.DateSent) },
            { k: 'Date of service', v: d(row.DateService) }, { k: 'Patient DOB', v: d(row.Birthdate) },
            { k: 'Patient zip', v: t(row.Zip) }, { k: 'Procedures attached', v: t(row.ProceduresAttached) },
            { k: 'Insurance estimate', v: m(row.InsPayEst) }, { k: 'Type', v: t(row.ClaimType) },
          ] });
          out.push({ key: 'subscriber', title: 'Subscriber', pairs: [
            { k: 'Subscriber ID', v: t(row.SubScriberID) }, { k: 'Subscriber', v: t(row.SubscriberName) },
            { k: 'Subscriber DOB', v: d(row.SubscriberBirthDate) }, { k: 'Carrier', v: t(row.CarrierName) },
            { k: 'Carrier phone', v: t(row.CarrierPhone) },
          ] });
        } else if (s.key === 'preauth') {
          out.push({ key: 'preauth', title: 'Pre-authorization', pairs: [
            { k: 'Sent', v: d(row.DateSent) }, { k: 'Provider', v: t(row.Provider) },
            { k: 'Procedures attached', v: t(row.AttachedProcedures) },
          ] });
          out.push({ key: 'carrier', title: 'Carrier & subscriber', pairs: [
            { k: 'Carrier', v: t(row.Carrier) }, { k: 'Carrier phone', v: t(row.Phone) },
            { k: 'Subscriber', v: t(row.SubscriberName) }, { k: 'Subscriber DOB', v: d(row.SubscriberBirthDate) },
            { k: 'Subscriber ID', v: t(row.SubScriberID) },
          ] });
        } else if (s.key === 'procedures') {
          out.push({ key: 'procedures', title: 'Pre-authorization', pairs: null, items: (s.rows || []).map(p => ({
            code: t(p.ProcedureCode),
            title: t(p.Description),
            meta: [p.Tooth ? `Tooth ${c.formatTooth(p.Tooth)}` : '', p.Provider || ''].filter(Boolean).join(' · '),
            right: m(p.InsuranceEstimate),
          })) });
        }
      });
      return out;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drtab__title { @include fu-dr-section-title; margin-bottom: 8px; }
.fu-drtab__none { margin: 0; padding: 12px 0; font-size: 13px; color: $fu-text-subtle; }

.fu-drdef {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  margin: 0;
  background: $fu-border;
  border: 1px solid $fu-border;
  border-radius: 12px;
  overflow: hidden;
}

.fu-drdef__cell {
  min-width: 0;
  padding: 10px 13px;
  background: $fu-surface;
  dt { font-size: 11.5px; font-weight: $fu-fw-regular; color: $fu-text-subtle; }
  dd { margin: 2px 0 0; font-size: 13.5px; font-weight: $fu-fw-bold; overflow-wrap: anywhere; }
}

.fu-drtab__item { @include fu-dr-item; }
.fu-drtab__code { @include fu-dr-code-chip; }
.fu-drtab__main { flex: 1; min-width: 0; }
.fu-drtab__item-title { @include fu-dr-item-title; }
.fu-drtab__meta { @include fu-dr-item-meta; }

.fu-drtab__right {
  flex: none;
  text-align: right;
  strong { @include fu-tabular; display: block; font-size: 14px; font-weight: 800; }
  small { display: block; margin-top: 1px; font-size: 12px; color: $fu-text-muted; }
}

.fu-dr--phone .fu-drdef { grid-template-columns: minmax(0, 1fr); }
</style>
